import { mergeAttributes } from '@tiptap/core';
import { type NodeType } from '@tiptap/pm/model';
import classNames from 'classnames';
import { Node_Type } from 'ricos-schema';
import { ListItemNodeChildrenPattern } from 'ricos-schema/next';
import {
  type DOMOutputSpec,
  type ExtensionProps,
  type NodeConfig,
  type RicosExtension,
  type RicosServices,
} from 'ricos-types';
import { createRicosNodeExtension } from 'wix-rich-content-plugin-commons';

import {
  createIndentationStylingDecorationPlugin,
  createListItemSizingDecorationPlugin,
  createListStylePositionDecorationPlugin,
} from '../decoration-plugins';
import styles from '../statics/styles.scss';
import {
  createMergingSiblingListNodesTransactionPlugin,
  createOffsetNormalizingTransactionPlugin,
  createRemoveTextWrapForListItemChildrenPlugin,
} from '../transaction-plugins';
import { indentList } from './commands/indentList';
import { outdentList } from './commands/outdentList';
import { toggleLists } from './commands/toggleLists';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    listItem: {
      /**
       * increase list indentation
       */
      indentList: () => ReturnType;
      /**
       * decrease list indentation
       */
      outdentList: () => ReturnType;
    };
    toggleLists: {
      /**
       * Toggle between different list types.
       */
      toggleLists: (
        listTypeOrName: string | NodeType,
        itemTypeOrName: string | NodeType
      ) => ReturnType;
    };
  }
}

export const listItem = createRicosNodeExtension({
  groups: ['shortcuts-enabled'],
  type: 'html-node',
  name: Node_Type.LIST_ITEM,
  reconfigure: (
    config: NodeConfig,
    _extensions: RicosExtension[],
    _props: ExtensionProps,
    settings: Record<string, unknown>,
    services: RicosServices
  ) => {
    return {
      ...config,
      addOptions() {
        return {
          services,
          HTMLAttributes: {
            class: styles.listItem,
            dir: 'auto',
          },
          ...settings,
        };
      },
    };
  },
  createExtensionConfig() {
    return {
      name: this.name,
      priority: 101,

      content: ListItemNodeChildrenPattern,

      defining: true,

      parseHTML() {
        return [
          {
            tag: 'li',
          },
        ];
      },

      renderHTML({ HTMLAttributes }) {
        const allAttributes = mergeAttributes(this.options.HTMLAttributes, HTMLAttributes);

        const attrs = {
          ...allAttributes,
          class: classNames(allAttributes.class),
          dir: 'auto',
        };

        return ['li', attrs, 0] as DOMOutputSpec;
      },

      addCommands() {
        return {
          toggleLists,
          indentList,
          outdentList,
        };
      },

      addKeyboardShortcuts() {
        const newLine = () => {
          return this.editor.commands.splitListItem(this.name);
        };

        const tiptapKeyboardShortcuts = {
          Enter: newLine,
        };

        //TODO: append extension specific code to existing new line command

        const newLineShortcuts: any = [];
        this.options.services.shortcuts.shortcuts.forEach(shortcut => {
          if (shortcut.shortcut.name === 'NewLine') {
            newLineShortcuts.push(shortcut);
          }
        });

        newLineShortcuts.forEach(newLineShortcut => {
          const modifier = newLineShortcut.keys.macOs.modifiers[0];
          const key = newLineShortcut.keys.macOs.key;
          const shortcutKey = modifier ? `${modifier}-${key}` : key;
          tiptapKeyboardShortcuts[shortcutKey] = newLine;
        });

        return {
          ...tiptapKeyboardShortcuts,
        };
      },
      addProseMirrorPlugins() {
        return [
          createIndentationStylingDecorationPlugin(),
          createListItemSizingDecorationPlugin(),
          createListStylePositionDecorationPlugin(),

          createMergingSiblingListNodesTransactionPlugin(),
          createOffsetNormalizingTransactionPlugin(),
          createRemoveTextWrapForListItemChildrenPlugin(),
        ];
      },
    };
  },
});
